import React, { useState } from "react";
import { Alert, Button, Card, CardHeader, Metric, ProgressBar, Table } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "./BroadcastEditContainer";
import HeatmapCard from "./HeatmapCard";
import { formatCount, formatPercent, prettyUrl, ratio } from "./Report/reportMath";

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    marginTop: 24,
    paddingBottom: 48,
    color: "var(--sl-text-primary)",
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 16,
  },
  bar: {
    padding: "8px 0",
  },
  barHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 4,
    fontSize: 14,
  },
  muted: {
    fontSize: 13,
    color: "var(--sl-text-tertiary)",
  },
};

const OverviewTab = () => {
  const { overviewMetrics, overviewLinkClicks } = useBroadcastEditContext();
  const [showAllLinks, setShowAllLinks] = useState(false);

  const metrics = overviewMetrics || {};
  const links = overviewLinkClicks || [];

  const sent = metrics.sent_count || 0;
  const delivered = metrics.delivered_count || 0;
  const opened = metrics.opened_count || 0;
  const clicked = metrics.clicked_count || 0;
  const failed = metrics.failed_count || 0;

  const totalTrackedClicks = links.reduce((sum, link) => sum + link.clicks, 0);
  const visibleLinks = showAllLinks ? links : links.slice(0, 5);

  const stats = [
    { label: "Emails Sent", value: formatCount(sent), footer: `${formatCount(sent)} recipients` },
    { label: "Delivered", value: formatPercent(delivered, sent), footer: `${formatCount(delivered)} delivered` },
    { label: "Bounced", value: formatPercent(metrics.bounced_count, delivered), footer: `${formatCount(metrics.bounced_count)} bounced` },
    { label: "Suppressed", value: formatPercent(metrics.suppressed_count, delivered), footer: `${formatCount(metrics.suppressed_count)} suppressed` },
    { label: "Failed", value: formatCount(failed), footer: failed > 0 ? `${formatCount(failed)} failed` : "None" },
  ];

  const bars = [
    { label: "Opened", detail: `${formatCount(opened)} opened`, value: ratio(opened, delivered) },
    { label: "Clicked", detail: `${formatCount(clicked)} clicked`, value: ratio(clicked, delivered) },
    { label: "Click to open", detail: `${formatCount(clicked)} clicked of opened`, value: ratio(clicked, opened) },
    { label: "Converted", detail: `${formatCount(metrics.converted_count)} converted`, value: ratio(metrics.converted_count, delivered) },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.metrics}>
        {stats.map((stat) => (
          <Metric key={stat.label} label={stat.label} value={stat.value} footer={<span style={styles.muted}>{stat.footer}</span>} />
        ))}
      </div>

      <Card header={<CardHeader title="Delivery" supportingText="All-time" />}>
        {bars.map((bar) => (
          <div key={bar.label} style={styles.bar}>
            <div style={styles.barHeader}>
              <span><strong>{bar.label}</strong> <span style={styles.muted}>{bar.detail}</span></span>
              <span>{bar.value.toFixed(1)}%</span>
            </div>
            <ProgressBar value={bar.value} label="none" aria-label={bar.label} />
          </div>
        ))}
        <div style={{ marginTop: 12 }}>
          <Alert
            color="gray"
            description="Opens and clicks come from the email pixel, which most clients now block — expect these to read low. Link clicks below are counted by the tracking redirect and are the more reliable signal."
          />
        </div>
      </Card>

      <Card
        padding="none"
        header={(
          <CardHeader
            title="Top clicked links"
            supportingText={`${formatCount(totalTrackedClicks)} tracked clicks across ${links.length} link${links.length === 1 ? "" : "s"}`}
            actions={links.length > 5 ? (
              <Button variant="link-color" size="sm" onClick={() => setShowAllLinks(!showAllLinks)}>
                {showAllLinks ? "Show less" : "View all"}
              </Button>
            ) : null}
          />
        )}
      >
        <Table
          stackBelow={560}
          columns={[
            { key: "rank", header: "#", width: 48, render: (link, index) => index + 1 },
            { key: "url", header: "Link", primary: true, render: (link) => <span title={link.url}>{prettyUrl(link.url)}</span> },
            { key: "clicks", header: "Total clicks", align: "right", render: (link) => formatCount(link.clicks) },
          ]}
          data={visibleLinks}
          getRowKey={(link) => link.url}
          empty={<div style={styles.muted}>No link clicks tracked yet</div>}
        />
      </Card>

      <HeatmapCard />
    </div>
  );
};

export default OverviewTab;
